import React, { useState, useEffect } from "react";
import { ValidationErrors } from "src/errors";
import { AuthFormAction, AuthFormData, AuthFormDataKey } from "src/types";
import { TextInputBox, FormLvlErrorMessage } from "src/components";
import { HEALF_IT_CONTACT_EMAIL } from "src/constants/emails";

interface AuthConfirmSignUpProps {
  formData: AuthFormData;
  validationErrors?: ValidationErrors<AuthFormDataKey>;
  formLvlErrorMessage: string;
  handleInputChange: (field: AuthFormDataKey, value: string) => void;
  setAction: (action: AuthFormAction) => void;
}

const AuthConfirmSignUp: React.FC<AuthConfirmSignUpProps> = ({
  formData,
  validationErrors,
  formLvlErrorMessage,
  handleInputChange,
  setAction
}) => {
  const [resendCount, setResendCount] = useState(0);
  const [resendTimer, setResendTimer] = useState(60);
  const resendDisableButton = resendCount >= 2;

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleResendClick = () => {
    setResendCount((prevCount) => prevCount + 1);
    setAction("RESEND_SIGN_UP");
  };

  return (
    <div className="p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-secondary-900 mb-2">Verify Your Email</h2>
        <p className="text-secondary-600 mb-2">Please check your email and input the code received.</p>
        <p className="text-secondary-500 text-sm">It may take few minutes to arrive.</p>
      </div>

      <div className="space-y-6">
        <div>
          <TextInputBox
            id="otp"
            label="Verification Code"
            placeholder="Enter the 6-digit code"
            value={formData.otp}
            onChange={(value: string) => handleInputChange("otp", value)}
            maxLength={6}
            error={validationErrors?.getErrorMessageByFormDataKey("otp")}
          />
        </div>

        <FormLvlErrorMessage message={formLvlErrorMessage} />

        <div className="space-y-4">
          <button
            className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            disabled={!formData.otp}
            type="submit"
          >
            Verify Email
          </button>

          <button
            className="w-full text-primary-600 hover:text-primary-700 disabled:text-primary-300 disabled:cursor-not-allowed font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={handleResendClick}
            type="submit"
            disabled={resendTimer > 0 || resendDisableButton}
          >
            {resendTimer > 0
              ? `Resend Code in ${resendTimer}s`
              : resendDisableButton
                ? "Resend Code (Max attempts reached)"
                : "Resend Code"}
          </button>
        </div>

        <div className="border-t border-secondary-200 text-center">
          <p className="text-secondary-500 text-sm">
            If you haven't received the code in 5 minutes or having trouble receiving your code? Contact our support
            team at{" "}
            <a
              href={`mailto:${HEALF_IT_CONTACT_EMAIL}`}
              className="text-primary-600 hover:text-primary-700 underline font-medium"
            >
              {HEALF_IT_CONTACT_EMAIL}
            </a>{" "}
            for assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export { AuthConfirmSignUp };
